import type { NextRouter } from "next/router";
import { useEffect, useRef } from "react";
import { PAGE_ROUTES } from "~/constants/pages";
import clientSide from "~/helpers/clientSide";
import { reportMonarchError } from "~/helpers/monarchErrorReporting";
import { clearMonarchSessionError, hasMonarchSessionError, isRecoverableMonarchError, runWhenIdle } from "~/helpers/monarchSessionRecovery";
type TagularWindow = Window & {
  tagular?: (eventName: string, ...args: unknown[]) => void;
};
const isBuyFlowRedirect = (): boolean => {
  const pathname = window.location.pathname.replace(/\/+$/, "");
  return pathname.endsWith(PAGE_ROUTES.BUY_FLOW_REDIRECT);
};

/**
 * Recovers from a transient Monarch client timeout / session-errored lock.
 *
 * Cohesion persists `monarchError` in localStorage after a client timeout, which
 * short-circuits every later evaluation for the rest of the session (including
 * Traffic Flow on /plan-package). This hook clears that flag immediately and
 * re-fires only the source-level evaluation after the browser is idle.
 *
 * The redirect page is allowed to navigate without a duplicate request; its
 * destination's normal page-view evaluation becomes the retry instead.
 */
export const useMonarchSessionRecovery = (router?: NextRouter | null): void => {
  const hasAttemptedRecovery = useRef(false);
  useEffect(() => {
    if (!clientSide || typeof window?.cohesion !== "function") return;
    let cancelScheduled: (() => void) | undefined;
    const attemptRecovery = (reason: string): boolean => {
      // Always unlock future soft-navigation evaluations, including after the
      // single explicit retry has already been consumed.
      clearMonarchSessionError();

      // /buy-flow-redirect has its own bounded navigation fallback. Starting a
      // second source request here would overlap the destination page request.
      if (isBuyFlowRedirect()) return false;
      if (hasAttemptedRecovery.current) return false;
      hasAttemptedRecovery.current = true;
      const recoveryUrl = window.location.href;
      cancelScheduled = runWhenIdle(() => {
        // A route change triggers its own auto-request with fresh URL context.
        if (window.location.href !== recoveryUrl) return;
        clearMonarchSessionError();

        // Trigger the source auto-request directly. Avoid pageViewed(), which
        // also emits a Beam event and adds another request on a constrained link.
        (window as TagularWindow).tagular?.("pageView", false, true);
        if (process.env.environment !== "production") {
          console.info("[Monarch session recovery] retried evaluation after idle", {
            reason
          });
        }
      });
      return true;
    };

    // Prior page already poisoned the session (e.g. timeout on /buy-flow-redirect).
    if (hasMonarchSessionError()) {
      const reason = "existing-monarchError";
      const recoveryScheduled = attemptRecovery(reason);
      reportMonarchError("session in errored state", {
        source: "session-recovery",
        extra: {
          monarchRecoveryReason: reason,
          monarchRecoveryScheduled: recoveryScheduled,
          routePath: window.location.pathname
        }
      });
    }
    const onMonarchDone = (err: unknown) => {
      if (!err) return;
      const recoverable = isRecoverableMonarchError(err);
      const recoveryScheduled = recoverable ? attemptRecovery("monarch:done") : false;
      reportMonarchError(err, {
        source: "done-event",
        extra: {
          monarchRecoverable: recoverable,
          monarchRecoveryScheduled: recoveryScheduled,
          routePath: window.location.pathname
        }
      });
    };
    window.cohesion("monarch:done", onMonarchDone);
    const onRouteChangeStart = () => {
      cancelScheduled?.();
      cancelScheduled = undefined;
      hasAttemptedRecovery.current = false;
    };
    router?.events?.on("routeChangeStart", onRouteChangeStart);
    return () => {
      cancelScheduled?.();
      router?.events?.off("routeChangeStart", onRouteChangeStart);
    };
  }, [router]);
};
export default useMonarchSessionRecovery;